import { t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import type {
  ExtractedDataUnion,
  ExtractedKybData,
  ExtractedKycData,
  ReportType,
} from '@repo/shared/schema';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/alert';
import { Badge } from '@repo/ui/badge';
import { Separator } from '@repo/ui/separator';
import { Skeleton } from '@repo/ui/skeleton';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  FileText,
  ShieldAlert,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import React from 'react';

function formatFieldKey(key: string) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^_/, '')
    .trim();
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const ExtractedDataSkeleton = () => {
  useLocale();
  return (
    <div className="flex flex-col gap-6">
      {[6, 4].map((count, gi) => (
        <div className="flex flex-col gap-3" key={gi}>
          <Skeleton className="h-3 w-32 rounded" />
          <div className="flex flex-col gap-3 rounded-lg border bg-muted/20 p-4">
            {Array.from({ length: count }).map((_, i) => (
              <div className="flex flex-col gap-1.5" key={i}>
                <Skeleton className="h-2 w-20" />
                <Skeleton className="h-4 w-full" />
                {i < count - 1 && <Separator className="mt-1" />}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Field ─────────────────────────────────────────────────────────────────────

const Field = ({ label, value }: { label: string; value: string | null | undefined }) => {
  useLocale();

  if (value == null || value === '') return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-[10px] text-muted-foreground/70 uppercase tracking-widest">
        {formatFieldKey(label)}
      </span>
      <span className="wrap-break-word text-foreground text-sm">{String(value)}</span>
    </div>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────

const Section = ({
  label,
  icon,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) => {
  useLocale();
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-muted-foreground/60">{icon}</span>
        <span className="font-semibold text-muted-foreground text-xs uppercase tracking-widest">
          {label}
        </span>
      </div>
      <div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3 shadow-xs">
        {children}
      </div>
    </div>
  );
};

const SectionDivider = () => {
  useLocale();
  return <Separator className="my-0.5" />;
};

// ── Validation Warnings ───────────────────────────────────────────────────────

const ExtractedDocumentWarnings = ({ extractedData }: { extractedData: ExtractedDataUnion }) => {
  useLocale();

  const hasWarnings = extractedData.documentWarnings && extractedData.documentWarnings.length > 0;

  const isExpiredBool = extractedData.isExpired;
  const isValidBool = extractedData.isValidDocument;

  return (
    <Section icon={<ShieldAlert size={13} />} label={t('reports.validation')}>
      <div className="flex flex-wrap items-center gap-2">
        {isValidBool != null && (
          <Badge
            className={
              isValidBool
                ? 'gap-1 border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : 'gap-1'
            }
            variant={isValidBool ? 'outline' : 'destructive'}
          >
            {isValidBool ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
            {isValidBool ? t('reports.validDocument') : t('reports.invalidDocument')}
          </Badge>
        )}
        {isExpiredBool != null && (
          <Badge
            className={
              !isExpiredBool
                ? 'gap-1 border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                : 'gap-1'
            }
            variant={isExpiredBool ? 'destructive' : 'outline'}
          >
            {isExpiredBool ? <XCircle size={11} /> : <CheckCircle2 size={11} />}
            {isExpiredBool ? t('reports.expired') : t('reports.notExpired')}
          </Badge>
        )}
        {extractedData.expiryDate && (
          <span className="text-muted-foreground text-xs">
            {t('reports.expires')}{' '}
            <span className="font-medium text-foreground">{extractedData.expiryDate}</span>
          </span>
        )}
      </div>

      {hasWarnings && (
        <Alert
          className="mt-0.5 gap-2 border-destructive/20 bg-destructive/5"
          variant="destructive"
        >
          <AlertTriangle size={14} />
          <AlertTitle className="font-semibold text-xs">{t('reports.documentWarnings')}</AlertTitle>
          <AlertDescription className="mt-0.5 text-xs">
            <ul className="list-inside list-disc space-y-0.5">
              {extractedData.documentWarnings!.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </Section>
  );
};

// ── KYC ──────────────────────────────────────────────────────────────────────

const ExtractedClientInformationData = ({
  extractedData,
}: {
  extractedData: Pick<ExtractedKycData, 'clientInformation'>;
}) => {
  useLocale();

  const info = extractedData.clientInformation;
  const fields: { label: string; value: string | null | undefined }[] = [
    {
      get label() {
        return t('reports.firstName');
      },
      value: info?.firstName,
    },
    {
      get label() {
        return t('reports.lastName');
      },
      value: info?.lastName,
    },
    {
      get label() {
        return t('reports.dateOfBirth');
      },
      value: info?.dateOfBirth,
    },
    {
      get label() {
        return t('reports.placeOfBirth');
      },
      value: info?.placeOfBirth,
    },
    {
      get label() {
        return t('reports.nationality');
      },
      value: info?.nationality,
    },
    {
      get label() {
        return t('reports.residenceCountry');
      },
      value: info?.residenceCountry,
    },
    {
      get label() {
        return t('reports.taxIdentificationNumber');
      },
      value: info?.taxIdentificationNumber,
    },
    {
      get label() {
        return t('reports.idNumber');
      },
      value: info?.idNumber,
    },
    {
      get label() {
        return t('reports.phone');
      },
      value: info?.phone,
    },
    {
      get label() {
        return t('reports.email');
      },
      value: info?.email,
    },
    {
      get label() {
        return t('reports.address');
      },
      value: info?.address,
    },
    {
      get label() {
        return t('reports.employmentStatus');
      },
      value: info?.employmentStatus,
    },
    {
      get label() {
        return t('reports.sourceOfFunds');
      },
      value: info?.sourceOfFunds,
    },
  ].filter((f) => f.value != null && f.value !== '');

  if (!fields.length) return null;

  return (
    <Section icon={<User size={13} />} label={t('reports.clientInformation')}>
      {fields.map((f, i) => (
        <React.Fragment key={f.label}>
          <Field label={f.label} value={f.value} />
          {i < fields.length - 1 && <SectionDivider />}
        </React.Fragment>
      ))}
    </Section>
  );
};

const ExtractedMRZData = ({
  extractedData,
}: {
  extractedData: Pick<ExtractedKycData, 'mrzData' | 'mrzFullString'>;
}) => {
  useLocale();

  const fields: { label: string; value: string | null | undefined }[] = [
    {
      get label() {
        return t('reports.documentNumber');
      },
      value: extractedData.mrzData?.documentNumber,
    },
    {
      get label() {
        return t('reports.nationality');
      },
      value: extractedData.mrzData?.nationality,
    },
    {
      get label() {
        return t('reports.dateOfBirth');
      },
      value: extractedData.mrzData?.dateOfBirth,
    },
    {
      get label() {
        return t('reports.expiryDate');
      },
      value: extractedData.mrzData?.expiryDate,
    },
    {
      get label() {
        return t('reports.sex');
      },
      value: extractedData.mrzData?.sex,
    },
  ].filter((f) => f.value != null && f.value !== '');

  const hasMrz = fields.length > 0 || extractedData.mrzFullString;
  if (!hasMrz) return null;

  return (
    <Section icon={<FileText size={13} />} label={t('reports.mrzData')}>
      {fields.map((f, i) => (
        <React.Fragment key={f.label}>
          <Field label={f.label} value={f.value} />
          {i < fields.length - 1 && <SectionDivider />}
        </React.Fragment>
      ))}
      {extractedData.mrzFullString && (
        <>
          {fields.length > 0 && <SectionDivider />}
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-[10px] text-muted-foreground/70 uppercase tracking-widest">
              {t('reports.mrzRaw')}{' '}
            </span>
            <code className="break-all rounded bg-muted px-2 py-1.5 font-mono text-[11px] text-muted-foreground leading-relaxed">
              {extractedData.mrzFullString}
            </code>
          </div>
        </>
      )}
    </Section>
  );
};

const KYCExtractedData = ({ extractedData }: { extractedData: ExtractedKycData }) => {
  useLocale();
  return (
    <div className="flex flex-col gap-4">
      <ExtractedDocumentWarnings extractedData={extractedData} />
      <ExtractedClientInformationData extractedData={extractedData} />
      <ExtractedMRZData extractedData={extractedData} />
    </div>
  );
};

// ── KYB ──────────────────────────────────────────────────────────────────────

const ExtractedCompanyInformationData = ({
  extractedData,
}: {
  extractedData: Pick<ExtractedKybData, 'companyInformation'>;
}) => {
  useLocale();

  const info = extractedData.companyInformation;
  const fields: { label: string; value: string | null | undefined }[] = [
    {
      get label() {
        return t('reports.companyName');
      },
      value: info?.companyName,
    },
    {
      get label() {
        return t('reports.companyRegistrationNumber');
      },
      value: info?.companyRegistrationNumber,
    },
    {
      get label() {
        return t('reports.companyIdNumber');
      },
      value: info?.companyIdNumber,
    },
    {
      get label() {
        return t('reports.companyTaxId');
      },
      value: info?.companyTaxId,
    },
    {
      get label() {
        return t('reports.sector');
      },
      value: info?.sector,
    },
    {
      get label() {
        return t('reports.industry');
      },
      value: info?.industry,
    },
    {
      get label() {
        return t('reports.countryOfIncorporation');
      },
      value: info?.countryOfIncorporation,
    },
  ].filter((f) => f.value != null && f.value !== '');

  if (!fields.length) return null;

  return (
    <Section icon={<Building2 size={13} />} label={t('reports.companyInformation')}>
      {fields.map((f, i) => (
        <React.Fragment key={f.label}>
          <Field label={f.label} value={f.value} />
          {i < fields.length - 1 && <SectionDivider />}
        </React.Fragment>
      ))}
    </Section>
  );
};

const ExtractedKeyMembersData = ({
  extractedData,
}: {
  extractedData: Pick<ExtractedKybData, 'keyMembers'>;
}) => {
  useLocale();

  const members = extractedData.keyMembers;
  if (!members?.length) return null;

  return (
    <Section icon={<Users size={13} />} label={t('reports.keyMembers')}>
      {members.map((member, memberIdx) => {
        const fields: { label: string; value: string | null | undefined }[] = [
          {
            get label() {
              return t('reports.firstName');
            },
            value: member.firstName,
          },
          {
            get label() {
              return t('reports.lastName');
            },
            value: member.lastName,
          },
          {
            get label() {
              return t('reports.dateOfBirth');
            },
            value: member.dateOfBirth,
          },
          {
            get label() {
              return t('reports.placeOfBirth');
            },
            value: member.placeOfBirth,
          },
          {
            get label() {
              return t('reports.nationality');
            },
            value: member.nationality,
          },
        ].filter((f) => f.value != null && f.value !== '');

        return (
          <React.Fragment key={memberIdx}>
            {memberIdx > 0 && <Separator className="my-1" />}
            <div className="flex flex-col gap-3">
              {member.memberRole && (
                <div className="flex items-center gap-2">
                  <Badge className="font-semibold text-[10px] tracking-wide" variant="secondary">
                    {member.memberRole}
                  </Badge>
                </div>
              )}
              {fields.map((f, i) => (
                <React.Fragment key={f.label}>
                  <Field label={f.label} value={f.value} />
                  {i < fields.length - 1 && <SectionDivider />}
                </React.Fragment>
              ))}
            </div>
          </React.Fragment>
        );
      })}
    </Section>
  );
};

const KYBExtractedData = ({ extractedData }: { extractedData: ExtractedKybData }) => {
  useLocale();
  return (
    <div className="flex flex-col gap-4">
      <ExtractedDocumentWarnings extractedData={extractedData} />
      <ExtractedCompanyInformationData extractedData={extractedData} />
      <ExtractedKeyMembersData extractedData={extractedData} />
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────

const EmptyExtractedData = () => {
  useLocale();
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="rounded-full bg-muted p-3">
        <FileText className="text-muted-foreground/50" size={18} />
      </div>
      <p className="text-muted-foreground text-sm">
        {t('reports.noExtractedDataAvailableForThisDocument')}
      </p>
    </div>
  );
};

// ── Public export ─────────────────────────────────────────────────────────────

export const ExtractedData = ({
  isExtracting,
  reportType,
  extractedData,
}: {
  isExtracting: boolean;
  reportType: ReportType;
  extractedData: ExtractedDataUnion | null | undefined;
}) => {
  useLocale();

  if (isExtracting) return <ExtractedDataSkeleton />;

  if (!extractedData || Object.keys(extractedData).length === 0) return <EmptyExtractedData />;

  return reportType === 'KYC' ? (
    <KYCExtractedData extractedData={extractedData as ExtractedKycData} />
  ) : (
    <KYBExtractedData extractedData={extractedData as ExtractedKybData} />
  );
};
